import { Component, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import App from "./App";

function getAmzEnvSplashConfig() {
    const env = window.AMZ_ENV || {};
    const enabledRaw = env.VITE_SPLASH_SCREEN_ENABLED;
    const durationRaw = env.VITE_SPLASH_SCREEN_DURATION;
    const titleRaw = env.VITE_SPLASH_SCREEN_TITLE;
    const subtitleRaw = env.VITE_SPLASH_SCREEN_SUBTITLE;
    const imageRaw = env.VITE_SPLASH_SCREEN_IMAGE;

    const enabled =
        enabledRaw === true ||
        enabledRaw === 1 ||
        enabledRaw === "1" ||
        enabledRaw === "true" ||
        enabledRaw === "True";

    const durationNum = Number(durationRaw);
    const duration = Number.isFinite(durationNum) && durationNum > 0 ? durationNum : 2500;

    const title = typeof titleRaw === "string" && titleRaw.trim() ? titleRaw.trim() : "";
    const subtitle = typeof subtitleRaw === "string" && subtitleRaw.trim() ? subtitleRaw.trim() : "";
    const image = typeof imageRaw === "string" && imageRaw.trim() ? imageRaw.trim() : "";

    return { enabled, duration, title, subtitle, image };
}

function isRootPath(pathnameRaw) {
    if (typeof pathnameRaw !== "string") return true;
    const segments = pathnameRaw.split("/").filter(Boolean);
    return segments.length === 0;
}

function SwipeToDismissButton({ onDismiss, isExiting }) {
    const TRACK_HORIZONTAL_PADDING = 4;
    const KNOB_SIZE = 52;
    const COMPLETE_THRESHOLD = 0.8;
    const trackRef = useRef(null);
    const pointerIdRef = useRef(null);
    const dragStartXRef = useRef(0);
    const dragStartOffsetRef = useRef(0);
    const dragOffsetRef = useRef(0);
    const maxDragRef = useRef(0);
    const [dragOffset, setDragOffset] = useState(0);
    const [maxDrag, setMaxDrag] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    const setDrag = useCallback((nextOffset) => {
        dragOffsetRef.current = nextOffset;
        setDragOffset(nextOffset);
    }, []);

    const measureTrack = useCallback(() => {
        if (!trackRef.current) return;
        const width = trackRef.current.getBoundingClientRect().width;
        const nextMaxDrag = Math.max(0, width - KNOB_SIZE - TRACK_HORIZONTAL_PADDING * 2);
        maxDragRef.current = nextMaxDrag;
        setMaxDrag(nextMaxDrag);
        if (dragOffsetRef.current > nextMaxDrag) {
            setDrag(nextMaxDrag);
        }
    }, [setDrag]);

    useEffect(() => {
        measureTrack();
        window.addEventListener("resize", measureTrack);
        return () => {
            window.removeEventListener("resize", measureTrack);
        };
    }, [measureTrack]);

    const resetDrag = useCallback(() => {
        setIsDragging(false);
        pointerIdRef.current = null;
        setDrag(0);
    }, [setDrag]);

    const completeDismiss = useCallback(() => {
        setIsDragging(false);
        pointerIdRef.current = null;
        setDrag(maxDragRef.current);
        onDismiss();
    }, [onDismiss, setDrag]);

    const handlePointerDown = useCallback((event) => {
        if (isExiting || maxDragRef.current <= 0) return;
        pointerIdRef.current = event.pointerId;
        dragStartXRef.current = event.clientX;
        dragStartOffsetRef.current = dragOffsetRef.current;
        setIsDragging(true);
        event.currentTarget.setPointerCapture?.(event.pointerId);
    }, [isExiting]);

    const handlePointerMove = useCallback((event) => {
        if (!isDragging || pointerIdRef.current !== event.pointerId) return;
        const delta = event.clientX - dragStartXRef.current;
        const nextOffset = Math.min(
            Math.max(0, dragStartOffsetRef.current + delta),
            maxDragRef.current,
        );
        setDrag(nextOffset);
    }, [isDragging, setDrag]);

    const handlePointerUp = useCallback((event) => {
        if (pointerIdRef.current !== event.pointerId) return;
        const completionPoint = maxDragRef.current * COMPLETE_THRESHOLD;
        if (dragOffsetRef.current >= completionPoint) {
            completeDismiss();
            return;
        }
        resetDrag();
    }, [completeDismiss, resetDrag]);

    const handlePointerCancel = useCallback((event) => {
        if (pointerIdRef.current !== event.pointerId) return;
        resetDrag();
    }, [resetDrag]);

    return (
        <div
            ref={trackRef}
            style={{
                width: "100%",
                marginTop: 20,
                height: 60,
                borderRadius: 999,
                border: "1px solid rgba(255,255,255,0.22)",
                background: "rgba(15,19,28,0.48)",
                position: "relative",
                overflow: "hidden",
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.05)",
                touchAction: "pan-y",
                userSelect: "none",
            }}
        >
            <div
                style={{
                    position: "absolute",
                    inset: 4,
                    borderRadius: 999,
                    width: KNOB_SIZE + dragOffset,
                    background: "linear-gradient(90deg, rgba(255,255,255,0.35), rgba(255,255,255,0.12))",
                    transition: isDragging ? "none" : "width 0.22s ease-out",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    color: "rgba(255,255,255,0.84)",
                    pointerEvents: "none",
                }}
            >
                밀어서 시작하기
            </div>
            <button
                type="button"
                aria-label="Swipe to dismiss splash"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
                disabled={isExiting}
                style={{
                    position: "absolute",
                    left: TRACK_HORIZONTAL_PADDING,
                    top: TRACK_HORIZONTAL_PADDING,
                    width: KNOB_SIZE,
                    height: KNOB_SIZE,
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.42)",
                    background: "linear-gradient(160deg, rgba(255,255,255,0.95), rgba(220,229,255,0.9))",
                    color: "#0f172a",
                    cursor: isExiting ? "default" : "grab",
                    transform: `translateX(${dragOffset}px)`,
                    transition: isDragging ? "none" : "transform 0.22s ease-out",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 20,
                    fontWeight: 700,
                    boxShadow: "0 8px 20px rgba(0,0,0,0.24)",
                    touchAction: "none",
                }}
            >
                ›
            </button>
        </div>
    );
}

function SplashOverlay({ title, subtitle, image, onDismiss, isExiting, showSwipeButton }) {
    const hasImage = Boolean(image);
    return (
        <div
            role={showSwipeButton ? undefined : "button"}
            tabIndex={showSwipeButton ? undefined : 0}
            aria-label="Splash"
            onClick={showSwipeButton ? undefined : onDismiss}
            onKeyDown={showSwipeButton ? undefined : (event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onDismiss();
                }
            }}
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 99999,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                cursor: showSwipeButton ? "default" : "pointer",
                border: 0,
                padding: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "#0b0e14",
                backgroundImage: hasImage
                    ? `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url('${image}')`
                    : "radial-gradient(1200px 600px at 30% 20%, rgba(74, 140, 255, 0.25), rgba(0,0,0,0) 60%), radial-gradient(900px 500px at 70% 80%, rgba(0, 204, 153, 0.18), rgba(0,0,0,0) 65%), linear-gradient(#0b0e14, #0b0e14)",
                backgroundSize: hasImage ? "cover" : "auto",
                backgroundPosition: "center",
                opacity: isExiting ? 0 : 1,
                transform: isExiting ? "scale(1.05)" : "scale(1)",
                transition: "opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                pointerEvents: isExiting ? "none" : "auto",
            }}
        >
            <div
                style={{
                    maxWidth: 520,
                    width: "calc(100% - 48px)",
                    color: "#ffffff",
                    textAlign: "center",
                    paddingBottom: 38,
                    opacity: isExiting ? 0 : 1,
                    transform: isExiting ? "translateY(10px)" : "translateY(0)",
                    transition: "opacity 0.4s ease-out, transform 0.4s ease-out",
                }}
            >
                <div style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 14px",
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.12)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                }}>
                    <span style={{
                        width: 8,
                        height: 8,
                        borderRadius: 99,
                        background: "rgba(255,255,255,0.85)",
                        boxShadow: "0 0 0 6px rgba(255,255,255,0.12)",
                    }} />
                    <span style={{ fontSize: 12, letterSpacing: "0.08em", opacity: 0.9 }}>LOADING</span>
                </div>
                <div style={{ marginTop: 18, fontSize: 24, fontWeight: 800, lineHeight: 1.15 }}>
                    {title || "잠시만 기다려주세요"}
                </div>
                {subtitle ? (
                    <div style={{ marginTop: 10, fontSize: 14, opacity: 0.86, lineHeight: 1.45 }}>
                        {subtitle}
                    </div>
                ) : null}
                {showSwipeButton ? (
                    <SwipeToDismissButton
                        onDismiss={onDismiss}
                        isExiting={isExiting}
                    />
                ) : (
                    <div style={{ marginTop: 18, fontSize: 12, opacity: 0.7 }}>
                        화면을 터치하면 바로 닫힙니다
                    </div>
                )}
            </div>
        </div>
    );
}

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, errorInfo: error.message };
    }

    componentDidCatch(error, errorInfo) {
        console.error("ErrorBoundary caught an error", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return <div>
                <h1>에러가 발생하였습니다.</h1>
                <p>에러가 발생했습니다. 새로고침 해주세요.</p>
                <p>{this.state.errorInfo}</p>
            </div>;
        }

        return this.props.children;
    }
}

function AppWrapper() {
    const splashConfig = useMemo(() => getAmzEnvSplashConfig(), []);
    const isRootLanding = useMemo(() => isRootPath(window.location.pathname), []);
    const showSwipeButton = isRootLanding;
    const [showSplash, setShowSplash] = useState(splashConfig.enabled);
    const [isExiting, setIsExiting] = useState(false);

    const handleDismiss = () => {
        setIsExiting(true);
        setTimeout(() => {
            setShowSplash(false);
            setIsExiting(false);
        }, 600); // Match CSS transition duration
    };

    useEffect(() => {
        if (!showSplash || isExiting) return;

        const handleEsc = (e) => {
            if (e.key === "Escape") {
                handleDismiss();
            }
        };

        let timer = null;
        if (!showSwipeButton) {
            timer = setTimeout(() => {
                handleDismiss();
            }, splashConfig.duration);
        }

        window.addEventListener("keydown", handleEsc);
        return () => {
            if (timer) clearTimeout(timer);
            window.removeEventListener("keydown", handleEsc);
        };
    }, [showSplash, isExiting, splashConfig.duration, showSwipeButton]);

    return (
        <ErrorBoundary >
            {(showSplash || isExiting) ? (
                <SplashOverlay
                    title={splashConfig.title}
                    subtitle={splashConfig.subtitle}
                    image={splashConfig.image}
                    onDismiss={handleDismiss}
                    isExiting={isExiting}
                    showSwipeButton={showSwipeButton}
                />
            ) : null}
            <App />
            <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} closeButton={false} limit={3} />
        </ErrorBoundary>
    );
}

export default AppWrapper;
